
import { defineComponent, onBeforeMount, reactive } from 'vue'
import moment from 'moment'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// module components
import RegionsActions from '@/modules/CitiesModule/components/RegionsActions/index.vue'

// types
import { RegionPayloadT, RegionListItemT } from '@/core/types/cities.types'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { RegionsColumns } from '@/core/constants/columns/Cities.columns'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        TheInput,
        RegionsActions,
        TheTable,
        TableScrollWrapper,
    },
    setup() {
        const notifications = useNotifications()
        const { actions_state, startCreate, startDelete, startUpdate, closeActions } =
            useActions<RegionListItemT>()

        const state = reactive(new DataState<RegionListItemT>({}))
        const regions_copy = reactive({ list: [] as RegionListItemT[], is_search: false })

        const filters_state = reactive({
            search_query: null as string | null,
            limit: 50,
        })

        const loadRegions = async (page: number, search?: string) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const data = await cities.getRegionsList(page, search, filters_state.limit)
                state.list = data.data
                regions_copy.list = data.data

                state.pagination.total_pages = data.meta.last_page
                state.pagination.total_items = data.meta.total

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadRegions(state.pagination.current_page)
        }

        onBeforeMount(() => loadRegions(1))

        const searchRegion = async () => {
            regions_copy.is_search = true

            if (!filters_state.search_query) {
                regions_copy.is_search = false
                return
            }

            await loadRegions(1, filters_state.search_query)

            regions_copy.is_search = false
        }

        const resetFilter = async () => {
            filters_state.search_query = null
            await loadRegions(1)
        }

        const createRegion = async (region_payload: RegionPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await cities.createRegion(region_payload)
                await loadRegions(state.pagination.current_page)

                closeActions()
                resetFilter()
                notifications.notify({ title: 'Регион успешно создан', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При создании региона возникла ошибка`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateRegion = async (region_id: number, region_payload: RegionPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await cities.updateRegion(region_id, region_payload)
                await loadRegions(state.pagination.current_page)

                closeActions()
                notifications.notify({ title: 'Регион успешно отредактирован', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При редактировании региона возникла ошибка`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteRegion = async (region_id: number) => {
            if (!region_id) return

            try {
                actions_state.is_actions_loading = true
                await cities.deleteRegion(region_id)

                loadRegions(1)
                closeActions()
                notifications.notify({ title: 'Регион успешно удален', type: 'success' })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `При удалении региона возникла ошибка`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        return {
            state,
            filters_state,
            actions_state,
            //
            regions_copy,
            moment,
            //
            startCreate,
            startDelete,
            startUpdate,
            resetFilter,
            //
            loadRegions,
            searchRegion,
            createRegion,
            updateRegion,
            deleteRegion,
            onLimitChange,
            //
            RegionsColumns,
        }
    },
})
